import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ExternalLink, Loader2, AlertCircle, CalendarDays } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { cn } from '@/lib/utils';
import { useClientProfile } from './useClientProfile';
import { ReferralChainCard } from './ReferralChainCard';
import { LinkedHouseholdPromotionsCard } from './LinkedHouseholdPromotionsCard';

/**
 * Cluster H v2 Fase 2.7 — overlay modal for nested ClientProfile preview.
 *
 * Replaces the variant-A target=_blank approach with an in-app overlay so
 * the user can drill into related clients (parent + promoted children)
 * without losing their current page context. Clicks inside the modal's
 * ReferralChainCard or LinkedHouseholdPromotionsCard REPLACE the modal's
 * clientId (single-modal navigation; no stacking) — managed by the parent
 * ClientProfilePage via setModalClientId.
 *
 * Animations: framer-motion AnimatePresence + scale/fade. useReducedMotion()
 * skips animations for users with prefers-reduced-motion: reduce.
 *
 * Accessibility:
 *   - role='dialog' aria-modal='true' aria-labelledby
 *   - Focus trap: enfocar X button at mount, cycle Tab within modal
 *   - ESC closes
 *   - Backdrop click closes
 *   - aria-label on X button + Open-full button
 *   - role='alert' on error state
 *
 * v1 limitation: browser back button does NOT close the modal (deferred
 * per spec; ESC + X + backdrop click cover the close paths).
 */

interface ClientProfileModalProps {
  /** When non-null, modal is open and fetches this client's profile. */
  clientId: string | null;
  /** Closes the modal (parent sets clientId to null). */
  onClose: () => void;
  /** Replaces the modal's clientId — used by inner cards to nested-navigate. */
  onNavigate: (clientId: string) => void;
}

export function ClientProfileModal({
  clientId,
  onClose,
  onNavigate,
}: ClientProfileModalProps): JSX.Element {
  const open = clientId !== null;
  useBodyScrollLock(open);

  // ESC close — registered once at mount; the handler reads `open` via closure
  // through `onClose` which the parent rebinds on each render.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && clientId != null ? (
        <ClientProfileModalInner
          key={clientId}
          clientId={clientId}
          onClose={onClose}
          onNavigate={onNavigate}
        />
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Inner — separate component so its useQuery + focus trap only mount when
// the modal is actually visible. Keyed by clientId so nested navigation
// (onNavigate) remounts the inner with a fresh query + re-runs focus init.
// ---------------------------------------------------------------------------

interface InnerProps {
  clientId: string;
  onClose: () => void;
  onNavigate: (clientId: string) => void;
}

function ClientProfileModalInner({
  clientId,
  onClose,
  onNavigate,
}: InnerProps): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = `client-profile-modal-${clientId}-title`;

  const { data, isLoading, isError } = useClientProfile(clientId);

  // Focus trap — focus the close button on mount, then trap Tab cycling
  // inside the modal. Simple implementation: query all focusable elements
  // each Tab press; wrap from last to first / first to last.
  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleOpenFull = (): void => {
    onClose();
    navigate(`/clients/${clientId}`);
  };

  // Animation variants — disabled when prefers-reduced-motion.
  const backdropVariants = reducedMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      };
  const modalVariants = reducedMotion
    ? { hidden: { opacity: 1, scale: 1 }, visible: { opacity: 1, scale: 1 }, exit: { opacity: 1, scale: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
        },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
      };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative max-h-[85vh] w-full max-w-2xl flex flex-col rounded-2xl bg-surface shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-ink-200 px-5 py-3 shrink-0">
          <p id={titleId} className="text-sm font-semibold text-ink-900">
            {t('profileModal.title')}
          </p>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t('profileModal.close')}
            className="p-1 rounded hover:bg-ink-100 text-ink-600 focus:outline-none focus:ring-2 focus:ring-lavender-400"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-lavender-400" aria-hidden />
              <p className="text-sm text-ink-600">{t('profileModal.loading')}</p>
            </div>
          ) : isError || !data ? (
            <div role="alert" className="flex items-start gap-2 text-sm text-danger-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
              <p>{t('profileModal.error')}</p>
            </div>
          ) : (
            <ModalBody data={data} onNavigate={onNavigate} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-5 py-3 shrink-0 bg-surface-subtle">
          <Button variant="primary" size="sm" onClick={handleOpenFull} disabled={!data}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t('profileModal.openFull')}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal body — condensed ClientProfile + ReferralChainCard +
// LinkedHouseholdPromotionsCard. Top 3 recent screenings.
// ---------------------------------------------------------------------------

interface ModalBodyProps {
  data: import('./clients.api').ClientProfile;
  onNavigate: (clientId: string) => void;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function dobYear(iso: string): string {
  if (!iso) return '—';
  return iso.slice(0, 4);
}

function ModalBody({ data, onNavigate }: ModalBodyProps): JSX.Element {
  const t = useT();
  const top3 = data.recentScreenings.slice(0, 3);
  return (
    <>
      {/* Identity row */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-lg font-semibold text-ink-900 truncate">
            {data.lastName}
            {data.firstName ? `, ${data.firstName}` : null}
          </p>
          <p className="text-xs text-ink-500 mt-0.5">
            {`DOB ${dobYear(data.dob)} · CIN …${data.cinLast6}`}
          </p>
        </div>
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border shrink-0',
            data.screeningStatus === 'screened'
              ? 'bg-mint-100 text-mint-900 border-mint-200'
              : 'bg-ink-100 text-ink-700 border-ink-200',
          )}
        >
          {data.screeningStatus}
        </span>
      </div>

      {/* Referral chain — inbound (peach) */}
      {data.referredBy ? (
        <ReferralChainCard
          parentClientId={data.referredBy.parentClientId}
          parentClientLastName={data.referredBy.parentClientLastName}
          parentClientCinLast6={data.referredBy.parentClientCinLast6}
          promotedAt={data.referredBy.promotedAt}
          promotedByAgentName={data.referredBy.promotedByAgentName}
          onParentClick={onNavigate}
        />
      ) : null}

      {/* Linked promotions — outbound (mint) */}
      <LinkedHouseholdPromotionsCard
        promotions={data.linkedHouseholdPromotions}
        onItemClick={onNavigate}
      />

      {/* Recent screenings — top 3 */}
      <div className="rounded-md border border-ink-200 bg-surface p-3 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-700">
          {t('profileModal.recentScreenings')}
        </p>
        {top3.length === 0 ? (
          <p className="text-xs text-ink-500">{t('profileModal.noScreenings')}</p>
        ) : (
          <ul className="space-y-1.5">
            {top3.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <CalendarDays className="h-3.5 w-3.5 text-ink-400 shrink-0" aria-hidden />
                  <span className="truncate text-ink-700">
                    {fmtDate(s.startedAt)}
                  </span>
                  {s.agentName ? (
                    <span className="text-ink-500 truncate">· {s.agentName}</span>
                  ) : null}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border shrink-0',
                    // T2-G (#151a) — un borrado (cancelado/barrido) ya no se
                    // pinta «in progress»: mismo defecto que en el perfil.
                    s.deleted === true
                      ? 'bg-ink-100 text-ink-700 border-ink-200'
                      : s.isSubmitted
                      ? 'bg-mint-100 text-mint-900 border-mint-200'
                      : s.completedAt
                      ? 'bg-sky-100 text-sky-900 border-sky-200'
                      : 'bg-amber-100 text-amber-900 border-amber-200',
                  )}
                >
                  {s.deleted === true ? 'cancelled' : s.isSubmitted ? 'submitted' : s.completedAt ? 'completed' : 'in progress'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
